import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { keyBy } from '@mews/fp';
import { createScopedReducer } from '@mews/framework';
import {
    ExtendedAvailabilityReportData,
    ExtendedTimeUnitData,
    ServiceData,
    ServiceOverbookingLimitReportData,
} from '@mews-commander/data-access';
import {
    TIME_UNIT_BY_RESOURCE_CATEGORY_DATA_SET,
    SORTED_RESOURCE_CATEGORY_IDS_SET,
    TOTAL_DATA_SET,
    TIME_UNIT_BY_SERVICE_DATA_SET,
    TIME_UNIT_TOTAL_DATA_SET,
    SERVICE_DATA_SET,
    RESOURCE_CATEGORY_TOTAL_DATA_SET,
    SERVICE_OVERBOOKING_LIMIT_DATA_SET,
} from '../actionTypes';
import { DataState, ReportMode, ScopedTimeUnitData } from '../types';

const initialState: DataState = {
    timeUnitByServiceData: {},
    serviceData: {},
    sortedResourceCategoryIds: [],
    timeUnitByResourceCategoryData: {},
    timeUnitTotalData: {},
    totalData: {} as DataState['totalData'],
    resourceCategoryTotalData: {},
    serviceOverbookingLimitData: {},
};

interface TotalDataPayload {
    totalData: ExtendedAvailabilityReportData;
    reportMode: ReportMode;
}

const dataReducer = createScopedReducer(initialState, {
    [TOTAL_DATA_SET]: (state: DataState, { totalData, reportMode }: TotalDataPayload) => {
        const newTotalData = pipe(state.totalData, record.upsertAt(reportMode, totalData));
        return {
            ...state,
            totalData: newTotalData,
        };
    },
    [SERVICE_DATA_SET]: (state: DataState, serviceData: Array<ServiceData>) => ({
        ...state,
        serviceData: keyBy('serviceId', serviceData),
    }),
    [SORTED_RESOURCE_CATEGORY_IDS_SET]: (state: DataState, sortedResourceCategoryIds: string[]) => ({
        ...state,
        sortedResourceCategoryIds,
    }),

    [TIME_UNIT_BY_RESOURCE_CATEGORY_DATA_SET]: (
        state: DataState,
        timeUnitByResourceCategoryData: ScopedTimeUnitData
    ) => ({
        ...state,
        timeUnitByResourceCategoryData,
    }),
    [TIME_UNIT_BY_SERVICE_DATA_SET]: (state: DataState, timeUnitByServiceData: ScopedTimeUnitData) => ({
        ...state,
        timeUnitByServiceData,
    }),
    [TIME_UNIT_TOTAL_DATA_SET]: (state: DataState, timeUnitTotalData: Array<ExtendedTimeUnitData>) => ({
        ...state,
        timeUnitTotalData: timeUnitTotalData.reduce(
            (acc: Record<string, ExtendedAvailabilityReportData>, { data, timeUnitBaseStartUtc }) => {
                acc[timeUnitBaseStartUtc] = data;
                return acc;
            },
            {}
        ),
    }),
    [RESOURCE_CATEGORY_TOTAL_DATA_SET]: (
        state: DataState,
        resourceCategoryTotalData: Array<{
            resourceCategoryId: string;
            data: ExtendedAvailabilityReportData;
        }>
    ) => ({
        ...state,
        resourceCategoryTotalData: resourceCategoryTotalData.reduce(
            (acc: Record<string, ExtendedAvailabilityReportData>, { data, resourceCategoryId }) => {
                acc[resourceCategoryId] = data;
                return acc;
            },
            {}
        ),
    }),
    [SERVICE_OVERBOOKING_LIMIT_DATA_SET]: (
        state: DataState,
        serviceOverbookingLimitData: Array<ServiceOverbookingLimitReportData>
    ) => ({
        ...state,
        serviceOverbookingLimitData: pipe(
            serviceOverbookingLimitData,
            array.reduce({}, (dataByServiceId, { data, serviceId }) =>
                pipe(
                    data,
                    array.reduce({}, (dataByDate, item) => ({
                        ...dataByDate,
                        [item.timeUnitBaseStartUtc]: item,
                    })),
                    (serviceData) => ({
                        ...dataByServiceId,
                        [serviceId]: serviceData,
                    })
                )
            )
        ),
    }),
});

export default dataReducer;
